import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { useTranslation } from "react-i18next";

interface LicenseEntry {
  name: string;
  version: string;
  license: string;
  url: string;
}

const licenses: LicenseEntry[] = [
  { name: "React", version: "18.3.1", license: "MIT", url: "https://github.com/facebook/react" },
  { name: "React DOM", version: "18.3.1", license: "MIT", url: "https://github.com/facebook/react" },
  { name: "React Router DOM", version: "7.12.0", license: "MIT", url: "https://github.com/remix-run/react-router" },
  { name: "Vite", version: "5.x", license: "MIT", url: "https://github.com/vitejs/vite" },
  { name: "TypeScript", version: "5.x", license: "Apache-2.0", url: "https://github.com/microsoft/TypeScript" },
  { name: "Tailwind CSS", version: "3.x", license: "MIT", url: "https://github.com/tailwindlabs/tailwindcss" },
  { name: "tailwindcss-animate", version: "1.0.7", license: "MIT", url: "https://github.com/jamiebuilds/tailwindcss-animate" },
  { name: "Radix UI", version: "various", license: "MIT", url: "https://github.com/radix-ui/primitives" },
  { name: "shadcn/ui", version: "various", license: "MIT", url: "https://github.com/shadcn-ui/ui" },
  { name: "Lucide React", version: "0.462.0", license: "ISC", url: "https://github.com/lucide-icons/lucide" },
  { name: "@tanstack/react-query", version: "5.83.0", license: "MIT", url: "https://github.com/TanStack/query" },
  { name: "@supabase/supabase-js", version: "2.78.0", license: "MIT", url: "https://github.com/supabase/supabase-js" },
  { name: "i18next", version: "25.8.0", license: "MIT", url: "https://github.com/i18next/i18next" },
  { name: "react-i18next", version: "16.5.3", license: "MIT", url: "https://github.com/i18next/react-i18next" },
  { name: "i18next-browser-languagedetector", version: "8.2.0", license: "MIT", url: "https://github.com/i18next/i18next-browser-languageDetector" },
  { name: "class-variance-authority", version: "0.7.1", license: "Apache-2.0", url: "https://github.com/joe-bell/cva" },
  { name: "clsx", version: "2.1.1", license: "MIT", url: "https://github.com/lukeed/clsx" },
  { name: "tailwind-merge", version: "2.6.0", license: "MIT", url: "https://github.com/dcastil/tailwind-merge" },
  { name: "cmdk", version: "1.1.1", license: "MIT", url: "https://github.com/pacocoursey/cmdk" },
  { name: "date-fns", version: "3.6.0", license: "MIT", url: "https://github.com/date-fns/date-fns" },
  { name: "embla-carousel-react", version: "8.6.0", license: "MIT", url: "https://github.com/davidjerleke/embla-carousel" },
  { name: "framer-motion (vaul)", version: "0.9.9", license: "MIT", url: "https://github.com/emilkowalski/vaul" },
  { name: "input-otp", version: "1.4.2", license: "MIT", url: "https://github.com/guilhermerodz/input-otp" },
  { name: "next-themes", version: "0.3.0", license: "MIT", url: "https://github.com/pacocoursey/next-themes" },
  { name: "react-day-picker", version: "8.10.1", license: "MIT", url: "https://github.com/gpbl/react-day-picker" },
  { name: "react-hook-form", version: "7.61.1", license: "MIT", url: "https://github.com/react-hook-form/react-hook-form" },
  { name: "react-resizable-panels", version: "2.1.9", license: "MIT", url: "https://github.com/bvaughn/react-resizable-panels" },
  { name: "recharts", version: "2.15.4", license: "MIT", url: "https://github.com/recharts/recharts" },
  { name: "sonner", version: "1.7.4", license: "MIT", url: "https://github.com/emilkowalski/sonner" },
  { name: "use-debounce", version: "10.0.6", license: "MIT", url: "https://github.com/xnimorz/use-debounce" },
  { name: "zod", version: "3.25.76", license: "MIT", url: "https://github.com/colinhacks/zod" },
];

const licenseAnchors: Record<string, string> = {
  "MIT": "mit",
  "Apache-2.0": "apache-2.0",
  "ISC": "isc",
};

const MIT_LICENSE = `MIT License

Copyright (c) <year> <copyright holders>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`;

const APACHE_LICENSE = `Apache License
Version 2.0, January 2004
http://www.apache.org/licenses/

TERMS AND CONDITIONS FOR USE, REPRODUCTION, AND DISTRIBUTION

1. Definitions.

"License" shall mean the terms and conditions for use, reproduction, and distribution as defined by Sections 1 through 9 of this document.

"Licensor" shall mean the copyright owner or entity authorized by the copyright owner that is granting the License.

"Legal Entity" shall mean the union of the acting entity and all other entities that control, are controlled by, or are under common control with that entity.

"You" (or "Your") shall mean an individual or Legal Entity exercising permissions granted by this License.

"Source" form shall mean the preferred form for making modifications, including but not limited to software source code, documentation source, and configuration files.

"Object" form shall mean any form resulting from mechanical transformation or translation of a Source form, including but not limited to compiled object code, generated documentation, and conversions to other media types.

"Work" shall mean the work of authorship, whether in Source or Object form, made available under the License.

"Derivative Works" shall mean any work, whether in Source or Object form, that is based on (or derived from) the Work.

"Contribution" shall mean any work of authorship submitted to the Licensor for inclusion in the Work.

"Contributor" shall mean Licensor and any Legal Entity on behalf of whom a Contribution has been received by the Licensor.

2. Grant of Copyright License. Subject to the terms and conditions of this License, each Contributor hereby grants to You a perpetual, worldwide, non-exclusive, no-charge, royalty-free, irrevocable copyright license to reproduce, prepare Derivative Works of, publicly display, publicly perform, sublicense, and distribute the Work and such Derivative Works in Source or Object form.

3. Grant of Patent License. Subject to the terms and conditions of this License, each Contributor hereby grants to You a perpetual, worldwide, non-exclusive, no-charge, royalty-free, irrevocable patent license to make, have made, use, offer to sell, sell, import, and otherwise transfer the Work.

4. Redistribution. You may reproduce and distribute copies of the Work or Derivative Works thereof in any medium, with or without modifications, and in Source or Object form, provided that You meet the following conditions:

(a) You must give any other recipients of the Work or Derivative Works a copy of this License; and

(b) You must cause any modified files to carry prominent notices stating that You changed the files; and

(c) You must retain, in the Source form of any Derivative Works that You distribute, all copyright, patent, trademark, and attribution notices from the Source form of the Work; and

(d) If the Work includes a "NOTICE" text file, You must include a readable copy of the attribution notices contained within such NOTICE file.

5. Submission of Contributions. Unless You explicitly state otherwise, any Contribution intentionally submitted for inclusion in the Work by You to the Licensor shall be under the terms and conditions of this License, without any additional terms or conditions.

6. Trademarks. This License does not grant permission to use the trade names, trademarks, service marks, or product names of the Licensor.

7. Disclaimer of Warranty. Unless required by applicable law or agreed to in writing, Licensor provides the Work on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND.

8. Limitation of Liability. In no event shall any Contributor be liable to You for damages, including any direct, indirect, special, incidental, or consequential damages of any character arising as a result of this License or out of the use or inability to use the Work.

9. Accepting Warranty or Additional Liability. While redistributing the Work, You may choose to offer acceptance of support, warranty, indemnity, or other liability obligations consistent with this License.

END OF TERMS AND CONDITIONS`;

const ISC_LICENSE = `ISC License

Copyright (c) <year> <copyright holders>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.`;

const fullLicenses = [
  { id: "mit", name: "MIT License", text: MIT_LICENSE },
  { id: "apache-2.0", name: "Apache License 2.0", text: APACHE_LICENSE },
  { id: "isc", name: "ISC License", text: ISC_LICENSE },
];

const OpenSourceLicenses = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Open Source Licenses – TestAssets"
        description="Open source software licenses used in the TestAssets application, including MIT, Apache 2.0, and ISC licensed packages."
        path="/open-source-licenses"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://testassets.io/" },
            { "@type": "ListItem", position: 2, name: "Open Source Licenses", item: "https://testassets.io/open-source-licenses" },
          ],
        }}
      />
      <LandingNav />

      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold text-foreground">
              {t("openSourceLicenses.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("openSourceLicenses.description")}
            </p>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="text-left px-4 py-3 font-medium text-foreground">
                    {t("openSourceLicenses.package")}
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-foreground hidden sm:table-cell">
                    {t("openSourceLicenses.version")}
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-foreground">
                    {t("openSourceLicenses.license")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((lib) => (
                  <tr key={lib.name} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <a
                        href={lib.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {lib.name}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      {lib.version}
                    </td>
                    <td className="px-4 py-3">
                      {licenseAnchors[lib.license] ? (
                        <a
                          href={`#${licenseAnchors[lib.license]}`}
                          className="text-primary hover:underline"
                        >
                          {lib.license}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">{lib.license}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground">
            {t("openSourceLicenses.footer")}
          </p>

          {/* Full license texts */}
          <div className="space-y-10 pt-4">
            <h2 className="text-2xl font-semibold text-foreground">
              {t("openSourceLicenses.fullTexts")}
            </h2>
            {fullLicenses.map((lic) => (
              <section key={lic.id} id={lic.id} className="space-y-3 scroll-mt-24">
                <h3 className="text-xl font-semibold text-foreground">{lic.name}</h3>
                <pre className="whitespace-pre-wrap text-xs text-muted-foreground bg-muted/30 rounded-lg border border-border p-4 overflow-x-auto">
                  {lic.text}
                </pre>
              </section>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OpenSourceLicenses;
